import { KINDE_ENVIRONMENT } from "./kindeEnvironment";

// Refresh the access token this many seconds before it actually expires, so a
// request that resolves its token right on the boundary still goes out with a
// valid one (covers clock skew + the in-flight request duration).
const REFRESH_SKEW_SECONDS = 60;

// The validity gate treats a token as unusable once it's within this many
// seconds of expiry. Deliberately small (not the 60s refresh window) so we only
// force a relogin when the token is genuinely about to die - the refresher owns
// the proactive-renew-while-still-valid case.
const VALIDITY_SKEW_SECONDS = 10;

// Kinde v5's refreshToken (re-exported from @kinde/js-utils via useKindeAuth)
// needs the auth domain + the app's client id; the provider doesn't bind them.
type KindeRefreshToken = (opts: {
  domain: string;
  clientId: string;
}) => Promise<unknown>;

interface TokenClaims {
  exp: number | null;
  azp: string | null;
}

const decodeTokenClaims = (token: string): TokenClaims | null => {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return {
      exp: typeof json.exp === "number" ? json.exp : null,
      azp: typeof json.azp === "string" ? json.azp : null,
    };
  } catch {
    // Malformed token segment - can't read claims.
    return null;
  }
};

/**
 * Builds the per-request "ensure the cached access token is fresh" check that
 * getAuthTokenBlocking runs before every BMA call. Kinde v5 no longer refreshes
 * the access token on its own (see KindeAuthenticatedUI), so we decode the
 * cached token's expiry and proactively refresh when it's within the skew
 * window. A still-fresh token is a cheap decode with no network call.
 *
 * Registered once per app via setTokenRefresher (mirrors setOnUnauthorized);
 * getAuthTokenBlocking coalesces concurrent callers onto a single refresh.
 */
export const makeEnsureFreshToken =
  (
    getToken: () => Promise<string | undefined>,
    refreshToken: KindeRefreshToken,
    clientId: string
  ) =>
  async (): Promise<void> => {
    const token = await getToken();
    // No token yet -> nothing to refresh; the relogin path owns this case.
    if (!token) return;
    const claims = decodeTokenClaims(token);
    if (claims === null || claims.exp === null) return;
    const secondsRemaining = claims.exp - Date.now() / 1000;
    if (secondsRemaining > REFRESH_SKEW_SECONDS) return;
    await refreshToken({
      domain: `https://${KINDE_ENVIRONMENT.KINDE_AUTH_DOMAIN}`,
      clientId,
    });
  };

/**
 * Builds the token-validity check getAuthTokenBlocking uses to decide whether
 * the cached token is safe to send. A token is usable only if it was minted by
 * THIS app's client (`azp`) and isn't expired - the two reasons BMA returns 401.
 *
 * Gating on validity (rather than mere presence) is what lets the "Session
 * expired" relogin modal actually block API calls. Kinde v5's getToken() returns
 * a present-but-invalid token - it doesn't clear it on expiry or when a refresh
 * fails - so a token that's expired (refresh failed, e.g. Firefox ETP blocking
 * the cross-subdomain refresh) or minted by another client (a stale bleed) would
 * otherwise sail through `if (!token)` and 401-loop behind the modal forever.
 *
 * Conservative by design: only reports unusable when CONFIDENTLY so (azp present
 * and mismatched, or exp present and past), so a valid token can never be
 * wrongly rejected into a relogin loop. Registered per app via setTokenValidator.
 */
export const makeTokenValidator =
  (clientId: string) =>
  (token: string): boolean => {
    const claims = decodeTokenClaims(token);
    if (claims === null) return false; // undecodable -> not a usable token
    if (claims.azp !== null && claims.azp !== clientId) return false; // wrong client
    if (
      claims.exp !== null &&
      claims.exp - Date.now() / 1000 <= VALIDITY_SKEW_SECONDS
    ) {
      return false; // expired (or within the in-flight skew of expiry)
    }
    return true;
  };
